import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Package, Truck, CheckCircle, MapPin, Calendar, CreditCard, Home } from 'lucide-react';

export default function Orders() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [allOrders, setAllOrders] = useState([]);

  useEffect(() => {
    // Load orders from localStorage
    const orders = JSON.parse(localStorage.getItem('scooby-orders') || '[]');
    setAllOrders(orders);

    if (orderId) {
      const foundOrder = orders.find(o => o.id === orderId);
      setOrder(foundOrder);
    }
  }, [orderId]);

  const formatPrice = (price) => `$${price.toFixed(2)}`;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Processing':
        return <Package className="w-6 h-6" />;
      case 'Shipped':
        return <Truck className="w-6 h-6" />;
      case 'Delivered':
        return <CheckCircle className="w-6 h-6" />;
      default:
        return <Package className="w-6 h-6" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processing':
        return 'from-orange-500 to-red-600';
      case 'Shipped':
        return 'from-blue-500 to-blue-600';
      case 'Delivered':
        return 'from-green-500 to-green-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const orderStatuses = [
    { label: 'Order Placed', status: 'Processing', completed: true },
    { label: 'Processing', status: 'Processing', completed: order?.status === 'Processing' || order?.status === 'Shipped' || order?.status === 'Delivered' },
    { label: 'Shipped', status: 'Shipped', completed: order?.status === 'Shipped' || order?.status === 'Delivered' },
    { label: 'Delivered', status: 'Delivered', completed: order?.status === 'Delivered' },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // If viewing a specific order
  if (orderId && order) {
    return (
      <div className="min-h-screen bg-black text-white py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            {/* Success Banner */}
            <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold mb-1">Order Confirmed!</h1>
                  <p className="text-green-400">Thank you for your purchase. Your order is being processed.</p>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <Package className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold">Order Number</h3>
                </div>
                <p className="text-gray-400 text-sm">{order.id}</p>
              </div>

              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold">Order Date</h3>
                </div>
                <p className="text-gray-400 text-sm">{formatDate(order.date)}</p>
              </div>

              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold">Total Amount</h3>
                </div>
                <p className="text-2xl font-bold text-orange-500">{formatPrice(order.total)}</p>
              </div>
            </div>

            {/* Order Status Timeline */}
            <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800 mb-8">
              <h2 className="text-xl font-bold mb-6">Order Status</h2>
              <div className="relative">
                {orderStatuses.map((item, index) => (
                  <div key={item.label} className="flex items-start gap-4 mb-6 last:mb-0">
                    <div className="relative flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.completed
                          ? `bg-gradient-to-r ${getStatusColor(item.status)}`
                          : 'bg-gray-800'
                      }`}>
                        {getStatusIcon(item.status)}
                      </div>
                      {index < orderStatuses.length - 1 && (
                        <div className={`w-0.5 h-12 mt-2 ${
                          item.completed ? 'bg-gradient-to-b from-orange-500 to-red-600' : 'bg-gray-700'
                        }`} />
                      )}
                    </div>
                    <div className="flex-1 pt-2">
                      <h3 className={`font-semibold ${item.completed ? 'text-white' : 'text-gray-500'}`}>
                        {item.label}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {item.completed && item.label === 'Order Placed' && formatDate(order.date)}
                        {item.completed && item.label === 'Processing' && 'Your order is being prepared'}
                        {item.completed && item.label === 'Shipped' && 'Package is on its way'}
                        {item.completed && item.label === 'Delivered' && 'Package has been delivered'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Order Items */}
              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800">
                <h2 className="text-xl font-bold mb-4">Order Items</h2>
                <div className="space-y-4">
                  {order.items.map((item) => (
                    <div key={item.cartItemId} className="flex gap-4 pb-4 border-b border-gray-800 last:border-0">
                      <div className="w-16 h-16 bg-gray-800 rounded-lg flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23374151" width="64" height="64"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">{item.name}</h3>
                        {item.variant && (
                          <p className="text-xs text-gray-400 mb-2">
                            {item.variant.size && `Size: ${item.variant.size}`}
                            {item.variant.size && item.variant.color && ' • '}
                            {item.variant.color && item.variant.color.name}
                            {item.variant.format && item.variant.format}
                          </p>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Qty: {item.quantity}</span>
                          <span className="font-bold text-orange-500">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  <h2 className="text-xl font-bold">Shipping Address</h2>
                </div>
                <div className="text-gray-400 space-y-1">
                  <p className="font-semibold text-white">{order.shippingAddress.name}</p>
                  <p>{order.shippingAddress.address}</p>
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                  </p>
                  <p>{order.shippingAddress.country}</p>
                  <p className="pt-2 border-t border-gray-800 mt-2">{order.shippingAddress.email}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 mt-8">
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <Button
                onClick={() => navigate('/merch')}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Continue Shopping
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // If viewing all orders or no specific order
  return (
    <div className="min-h-screen bg-black text-white py-20 px-6">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <h1 className="text-4xl font-bold mb-8">My Orders</h1>

          {allOrders.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">No orders yet</h2>
              <p className="text-gray-400 mb-6">Start shopping to see your orders here</p>
              <Button
                onClick={() => navigate('/merch')}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              >
                Browse Merchandise
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {allOrders.reverse().map((order) => (
                <div
                  key={order.id}
                  className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl p-6 border border-gray-800 hover:border-orange-500/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-lg mb-1">Order {order.id}</h3>
                      <p className="text-sm text-gray-400">{formatDate(order.date)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${getStatusColor(order.status)} text-white text-sm font-semibold`}>
                        {order.status}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Total</p>
                        <p className="text-xl font-bold text-orange-500">{formatPrice(order.total)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 overflow-x-auto">
                    {order.items.slice(0, 5).map((item, index) => (
                      <div key={index} className="w-12 h-12 bg-gray-800 rounded flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover rounded"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48"%3E%3Crect fill="%23374151" width="48" height="48"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    ))}
                    {order.items.length > 5 && (
                      <div className="w-12 h-12 bg-gray-800 rounded flex items-center justify-center text-xs text-gray-400">
                        +{order.items.length - 5}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
